/**
 * Wraps the application with Okta-based authorization.
 * The wrapper initializes the auth client, handles login redirects, and
 * renders the app only after authentication succeeds.
 * @flow
 */
import React, {
  Suspense, useEffect, useState, useRef, useMemo
} from 'react';
import { useDispatch } from 'react-redux';
import SessionContext from '../../features/sessionContext/sessionContextSlice';
import DPOMAuth from '../../DPOMAuth';
import { setAuthInstance } from '../../services/authService';
import imagesPath from '../../constants/imagesPath';
import NikeSpinner from '../../nikeCustomComponents/nikeSpinner';
import NotificationDialog from '../NotificationDialog';
import './styles.scss';
import messageText from '../../constants/messageText';
import { validatePathName } from '../../helpers/dataHelpers';
import { prodOkta } from '../../helpers/envServer';
import { setAuthKey } from '../../features/auth/authSlice';
import { settingsActions } from '../../features/profileSettings/settingSlice';
import { featureFlagActions } from '../../features/featureFlag/featureFlagSlice';
import {
  getOktaAccessTokenExpiresInMs,
  processAuthorizationOnInit
} from './withAuthorizationHelpers';

const App = React.lazy(() => import('../../App'));

export { getOktaAccessTokenExpiresInMs } from './withAuthorizationHelpers';

/** In-memory fallback used when browser storage is unavailable. */
const memoryStorageFallback = new Map();

/**
 * Creates the storage adapter used by the Okta client.
 * It reads from localStorage first, then the in-memory fallback, then Redux.
 */
function createOktaStorage(dispatch, getAuthKeyFn, setOktaValueFn) {
  return {
    getItem(key) {
      try {
        const fromStorage = localStorage.getItem(key);
        if (fromStorage) return fromStorage;
        const fromMemory = memoryStorageFallback.get(key);
        if (fromMemory) return fromMemory;
        const fromRedux = dispatch(getAuthKeyFn(key));
        return fromRedux ?? null;
      } catch (e) {
        console.log('Okta storage read error:', e);
        const fromMemory = memoryStorageFallback.get(key);
        return fromMemory ?? null;
      }
    },
    setItem(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        memoryStorageFallback.set(key, value);
      }
      try {
        setOktaValueFn(key, value, dispatch);
      } catch (e) {
        console.log('Okta storage Redux sync error:', e);
      }
    },
    removeItem(key) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        memoryStorageFallback.delete(key);
      }
    }
  };
}

/** Reads a stored auth value from Redux. */
export const getAuthKey = (key: string) => (dispatch: Object, getState: Function) => {
  const state = getState();
  try {
    return state.auth[key];
  } catch (e) {
    console.log('Err', e);
  }
  return null;
};

/** Persists an auth value into Redux. */
export const setOktaValue = (key: string, value: Object, dispatch: Object) => {
  dispatch(setAuthKey({ [key]: value }));
};

/** Initializes authentication and renders the app when login succeeds. */
export function WithAuthorization() {
  const dispatch = useDispatch();
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [email, setEmail] = useState(null);
  const loginAttempted = useRef(false);
  const initialized = useRef(false);
  const sessionRefreshTimerRef = useRef(null);

  /** Clears the scheduled token-aligned refresh timer. */
  const clearSessionRefreshTimer = () => {
    if (sessionRefreshTimerRef.current != null) {
      clearTimeout(sessionRefreshTimerRef.current);
      sessionRefreshTimerRef.current = null;
    }
  };

  const redirectUri = window.location.href.split('?')[0];
  const uriWithTrailingSlash = (redirectUri.endsWith('/')) ? redirectUri : `${redirectUri}/`;

  /** Creates the shared DPOM auth client instance for this session. */
  const DPOMAuthClient = useMemo(() => {
    const client = new DPOMAuth({
      clientId: process.env.REACT_APP_DPOM_CLIENT_ID,
      prod: prodOkta,
      storage: createOktaStorage(dispatch, getAuthKey, setOktaValue),
      redirectUri: uriWithTrailingSlash
    });
    setAuthInstance(client);
    return client;
  }, [uriWithTrailingSlash, prodOkta]);

  /** Refreshes feature-flag data after auth state changes. */
  const fetchFeatureFlag = () => dispatch(featureFlagActions.fetchProviderData());
  /** Marks the active-list cache for reload. */
  const updateReloadActiveList = (value) => dispatch(
    featureFlagActions.updateReloadActiveList(value)
  );

  /** Resets the profile-refresh timestamp. */
  const userProfileUpdateRefreshTime = () => dispatch(
    settingsActions.userProfileUpdateRefreshTime()
  );

  /**
   * Schedules a refresh aligned to the Okta access-token expiration.
   * When the timer fires, it refreshes user/profile state and reschedules.
   */
  const scheduleSessionAlignedRefresh = () => {
    clearSessionRefreshTimer();
    const scheduleNext = async () => {
      const delayMs = await getOktaAccessTokenExpiresInMs(
        () => DPOMAuthClient.getAccessToken()
      );
      sessionRefreshTimerRef.current = window.setTimeout(() => {
        userProfileUpdateRefreshTime();
        fetchFeatureFlag();
        updateReloadActiveList(true);
        scheduleNext();
      }, delayMs);
    };
    scheduleNext();
  };

  /** Starts the auth client when the wrapper mounts. */
  useEffect(() => {
    DPOMAuthClient.start().catch((err) => console.log('TokenManager start error:', err));
  }, [DPOMAuthClient]);

  /**
   * Runs the one-time authorization bootstrap and falls back to login when
   * auth state does not resolve in time.
   */
  useEffect(() => {
    if (initialized.current) {
      return undefined;
    }
    initialized.current = true;

    const userProfileUpdateTime = () => dispatch(settingsActions.userProfileUpdateTime());

    processAuthorizationOnInit({
      authClient: DPOMAuthClient,
      validatePathNameFn: validatePathName,
      hasLoginBeenAttempted: () => loginAttempted.current,
      markLoginAttempted: () => {
        loginAttempted.current = true;
      },
      setIsLoggedIn,
      setEmail,
      userProfileUpdateTime,
      fetchFeatureFlag,
      scheduleSessionAlignedRefresh
    });

    const timeoutId = setTimeout(() => {
      setIsLoggedIn((currentState) => {
        if (currentState === null) {
          if (!loginAttempted.current) {
            loginAttempted.current = true;
            DPOMAuthClient.login();
          }
          return false;
        }
        return currentState;
      });
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      clearSessionRefreshTimer();
    };
  }, []);

  /** Shows the loading spinner while auth state is unresolved. */
  if (isLoggedIn === null) {
    return (
      <NikeSpinner className="loginSpinner" open={isLoggedIn === null}>
        {messageText.authReq}
      </NikeSpinner>
    );
  }

  /** Shows a failure dialog when login cannot be completed. */
  if (!isLoggedIn) {
    return (
      <NotificationDialog
        open
        className="loginNotification"
        image={imagesPath.nikeShoeBox}
        title={messageText.authFailed}
        description={messageText.authMsg}
      />
    );
  }

  return (
    <SessionContext.Provider value={email}>
      <Suspense fallback={<div />}>
        <App session={{ email, isLoggedIn }} />
      </Suspense>
    </SessionContext.Provider>
  );
}

export default function AuthWrapper() {
  return <WithAuthorization />;
}
